import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { bookingService } from '../../services/bookingService';

interface Guest {
    name: string;
    email: string;
    phoneNumber: string;
}

interface BookingData {
    personalInfo: {
        name: string;
        phoneNumber: string;
        email: string;
        notificationPreference: string;
        message?: string;
    };
    retreat: string;
    selectedDate: {
        startDate: string;
        endDate: string;
    };
    numberOfGuests: number;
    guests: Guest[];
    selectedAccommodation: string;
    paymentInfo: {
        paymentType: 'full' | 'deposit';

        paymentMethod: 'card' | 'bank_transfer';
        currency: string;
        cardInfo?: {
            cardNumber: string;
            expiryMonth: string;
            expiryYear: string;
            cvv: string;
            nameOnCard: string;
        };
    };
}

interface BookingState {
    currentBooking: BookingData | null;
    bookings: any[];
    selectedBooking: any | null;
    isLoading: boolean;
    error: string | null;
    success: boolean;
    pagination: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
    };
}

const initialState: BookingState = {
    currentBooking: null,
    bookings: [],
    selectedBooking: null,
    isLoading: false,
    error: null,
    success: false,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
    },
};

// Async thunks
export const createBooking = createAsyncThunk(
    'booking/create',
    async (bookingData: BookingData, { rejectWithValue }) => {
        try {
            const response = await bookingService.createBooking(bookingData);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create booking');
        }
    }
);

export const getBookingById = createAsyncThunk(
    'booking/getById',
    async (bookingId: string, { rejectWithValue }) => {
        try {
            const response = await bookingService.getBookingById(bookingId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking');
        }
    }
);

export const getUserBookings = createAsyncThunk(
    'booking/getUserBookings',
    async ({ page = 1, limit = 10 }: { page?: number; limit?: number }, { rejectWithValue }) => {
        try {
            const response = await bookingService.getUserBookings(page, limit);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
        }
    }
);

export const cancelBooking = createAsyncThunk(
    'booking/cancel',
    async (bookingId: string, { rejectWithValue }) => {
        try {
            const response = await bookingService.cancelBooking(bookingId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to cancel booking');
        }
    }
);

export const updateBooking = createAsyncThunk(
    'booking/update',
    async ({ bookingId, updateData }: { bookingId: string; updateData: Partial<BookingData> }, { rejectWithValue }) => {
        try {
            const response = await bookingService.updateBooking(bookingId, updateData);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
        }
    }
);

const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        },
        setCurrentBooking: (state, action: PayloadAction<BookingData>) => {
            state.currentBooking = action.payload;
        },
        updateCurrentBooking: (state, action: PayloadAction<Partial<BookingData>>) => {
            if (state.currentBooking) {
                state.currentBooking = { ...state.currentBooking, ...action.payload };
            }
        },
        clearCurrentBooking: (state) => {
            state.currentBooking = null;
        },
        resetBookingState: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            // Create booking
            .addCase(createBooking.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
                state.error = null;
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.success = false;
            })

            // Get booking by ID
            .addCase(getBookingById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getBookingById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedBooking = action.payload.data;
                state.error = null;
            })
            .addCase(getBookingById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Get user bookings
            .addCase(getUserBookings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getUserBookings.fulfilled, (state, action) => {
                state.isLoading = false;
                state.bookings = action.payload.data;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(getUserBookings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Cancel booking
            .addCase(cancelBooking.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(cancelBooking.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update the booking status in the list
                state.bookings = state.bookings.map(booking =>
                    booking.id === action.meta.arg ? { ...booking, status: 'cancelled' } : booking
                );
                state.error = null;
            })
            .addCase(cancelBooking.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Update booking
            .addCase(updateBooking.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateBooking.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedBooking = action.payload.data;
                state.error = null;
            })
            .addCase(updateBooking.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    clearError,
    clearSuccess,
    setCurrentBooking,
    updateCurrentBooking,
    clearCurrentBooking,
    resetBookingState,
} = bookingSlice.actions;

export default bookingSlice.reducer;